// src/components/exercise/ContentViewer.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useExerciseStore } from '../../stores/useExerciseStore';
import { useAuthStore }     from '../../stores/useAuthStore';
import { getPaidUnitPagesUrls, getFreeUnitPagesUrls, isFreeUnit } from '../../services/paymentService';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore }   from '../../stores/useThemeStore';

const ContentViewer: React.FC = () => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const currentUnitId = useExerciseStore(s => s.currentUnitId);
  const currentBookId = useExerciseStore(s => s.currentBookId);
  const profile       = useAuthStore(s => s.profile);

  const [pages,      setPages]      = useState<string[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [activePage, setActivePage] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentUnitId) return;
    setLoading(true);
    setError(null);
    setPages([]);
    setActivePage(0);

    const load = async () => {
      try {
        const free       = isFreeUnit(currentBookId ?? '', currentUnitId);
        const subscribed = profile?.subscription?.status === 'active' || (profile as any)?.hasFullAccess === true;
        let urls: string[] | null;
        if (free) {
          urls = await getFreeUnitPagesUrls(currentBookId ?? '', currentUnitId);
        } else if (subscribed && currentBookId) {
          urls = await getPaidUnitPagesUrls(currentBookId, currentUnitId);
        } else {
          setError('Upgrade your plan to unlock this content.');
          setLoading(false);
          return;
        }
        if (!urls || !urls.length) setError('No content pages found for this unit.');
        else setPages(urls);
      } catch (err) {
        console.error('[ContentViewer] fetch failed:', err);
        setError('Failed to load content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [currentUnitId, currentBookId, profile]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || pages.length === 0) return;
    const fraction = el.scrollTop / (el.scrollHeight - el.clientHeight || 1);
    const idx = Math.round(fraction * (pages.length - 1));
    setActivePage(Math.max(0, Math.min(idx, pages.length - 1)));
  };

  const scrollToPage = (idx: number) => {
    const el = scrollRef.current;
    if (!el || pages.length < 2) return;
    const fraction = idx / (pages.length - 1);
    el.scrollTo({ top: fraction * (el.scrollHeight - el.clientHeight), behavior: 'smooth' });
    setActivePage(idx);
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: C.surface, color: C.muted }}>
        <style>{`@keyframes _cv_spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          border: `2px solid ${C.border}`,
          borderTopColor: C.accent,
          animation: '_cv_spin 0.75s linear infinite',
        }} />
        <span style={{ fontSize: '0.82rem', letterSpacing: '0.06em' }}>Loading content</span>
      </div>
    );
  }

  if (error || pages.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: C.surface, padding: '32px 24px', textAlign: 'center', color: C.muted }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          background: C.card, border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </div>
        <div style={{ fontSize: '0.88rem', lineHeight: 1.6, maxWidth: '260px' }}>
          {error ?? 'No content available for this unit.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: C.surface }}>
      {/* Header */}
      <div style={{
        padding:        '10px 18px',
        borderBottom:   `1px solid ${C.border}`,
        background:     isDark ? 'rgba(13,18,32,0.8)' : 'rgba(248,250,252,0.95)',
        backdropFilter: 'blur(12px)',
        display:        'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink:     0,
      }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: C.accent, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Unit Content
        </div>
        <div style={{ fontSize: '0.74rem', color: C.muted }}>
          <span style={{ color: C.text, fontWeight: 700 }}>{activePage + 1}</span>
          {' / '}{pages.length}
        </div>
      </div>

      {/* Images */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {pages.map((url, idx) => (
          <img
            key={idx}
            src={url}
            alt={`Page ${idx + 1}`}
            loading={idx < 2 ? 'eager' : 'lazy'}
            decoding="async"
            style={{
              width: '100%', borderRadius: '10px',
              border: `1px solid ${C.border}`,
              display: 'block',
              background: C.card,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          />
        ))}
        <div style={{ height: '4px', flexShrink: 0 }} />
      </div>

      {/* Page dots */}
      {pages.length > 1 && pages.length <= 12 && (
        <div style={{
          padding: '10px 14px', borderTop: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexShrink: 0,
        }}>
          {pages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToPage(idx)}
              title={`Page ${idx + 1}`}
              style={{
                width:        activePage === idx ? '24px' : '7px',
                height:       '7px',
                borderRadius: '4px',
                border:       'none',
                background:   activePage === idx
                  ? C.accent
                  : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'),
                cursor:       'pointer',
                padding:      0,
                transition:   'all 0.25s ease',
                boxShadow:    activePage === idx ? `0 0 8px ${C.accent}60` : 'none',
              }}
            />
          ))}
        </div>
      )}

      {pages.length > 12 && (
        <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {pages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollToPage(idx)}
                style={{
                  width: '22px', height: '22px', borderRadius: '6px',
                  border: `1px solid ${activePage === idx ? C.accent : 'transparent'}`,
                  background: activePage === idx ? 'rgba(79,140,247,0.15)' : 'transparent',
                  color: activePage === idx ? C.accent : C.muted,
                  fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.15s ease', padding: 0,
                }}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentViewer;
